// Themed Excel + PDF export helpers for the ticket pages
// (Support / Defect / Idea). Same navy-band + orange-period + slate-meta
// look used by dNews History, dSpr Email Logs, and the Reports server.
//
// Each ticket page calls these with its own columns + rows; the chrome
// (brand band, meta block, table header, zebra rows, status colouring)
// is centralised here so the three exports stay visually identical.

import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addEagleWatermark } from "./pdfWatermark";

// Palette - identical hex values across Excel + PDF.
const COL = {
    navy:   { argb: "FF0D1B2A", rgb: [13, 27, 42] },
    orange: { argb: "FFE8520A", rgb: [232, 82, 10] },
    sub:    { argb: "FFAABBCC", rgb: [170, 187, 204] },
    slate:  { argb: "FF64748B", rgb: [100, 116, 139] },
    line:   { argb: "FFE2E8F0", rgb: [226, 232, 240] },
    zebra:  { argb: "FFFAFBFD", rgb: [250, 251, 253] },
};

function fmtTs(d) {
    if (!d) return "";
    try {
        const x = new Date(d);
        return x.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
    } catch { return String(d); }
}

// ── EXCEL ────────────────────────────────────────────────────────────
// columns: [{ key, label, width, wrap? }]
// rows:    [{ <key>: value, ... }]
// status field (optional): name of the column whose value should be
// colour-bolded in the body (Open=blue, Closed=green, Cancelled=red).
export async function exportTicketsExcel({
    moduleName,       // e.g. "dAssist"
    sheetTitle,       // e.g. "Support Tickets"
    columns,
    rows,
    filterLabel,
    statusKey,
    filename,
}) {
    const wb = new ExcelJS.Workbook();
    wb.creator = `Dolluz Corp · ${moduleName}`;
    wb.created = new Date();
    const ws = wb.addWorksheet(sheetTitle);
    const cols = columns.length;

    ws.columns = columns.map(c => ({ key: c.key, width: c.width || 20 }));

    // Navy header band ("Dolluz Corp." + orange dot + "  moduleName")
    const brandRow = ws.addRow([]);
    ws.mergeCells(brandRow.number, 1, brandRow.number, cols);
    const brandCell = brandRow.getCell(1);
    brandCell.value = {
        richText: [
            { text: "Dolluz Corp",       font: { bold: true, color: { argb: "FFFFFFFF" }, size: 16, name: "Calibri" } },
            { text: ".",                 font: { bold: true, color: { argb: COL.orange.argb }, size: 16, name: "Calibri" } },
            { text: `    ${moduleName}`, font: { bold: true, color: { argb: COL.orange.argb }, size: 16, name: "Calibri" } },
        ],
    };
    brandCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.navy.argb } };
    brandCell.alignment = { vertical: "middle", indent: 1 };
    brandRow.height = 28;

    // Sub-band under the brand
    const subRow = ws.addRow([sheetTitle]);
    ws.mergeCells(subRow.number, 1, subRow.number, cols);
    const subCell = subRow.getCell(1);
    subCell.font = { color: { argb: COL.sub.argb }, size: 10, name: "Calibri" };
    subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.navy.argb } };
    subCell.alignment = { vertical: "middle", indent: 1 };
    subRow.height = 18;

    ws.addRow([]);

    // Meta block - filter (if any), generated timestamp, row count
    const metaRows = [
        ...(filterLabel ? [["Filter:",    filterLabel]] : []),
        ["Generated:", fmtTs(new Date().toISOString()) + " IST"],
        ["Rows:",      String(rows.length)],
    ];
    metaRows.forEach(([k, v]) => {
        const row = ws.addRow([k, v]);
        row.getCell(1).font = { color: { argb: COL.slate.argb }, size: 10 };
        row.getCell(2).font = { bold: true, color: { argb: COL.navy.argb }, size: 10 };
    });
    ws.addRow([]);

    // Table header
    const hdrRow = ws.addRow(columns.map(c => c.label));
    hdrRow.eachCell(cell => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 10.5, name: "Calibri" };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.navy.argb } };
        cell.alignment = { vertical: "middle", indent: 1 };
        cell.border = { bottom: { style: "thin", color: { argb: COL.line.argb } } };
    });
    hdrRow.height = 22;

    // Body
    rows.forEach((r, i) => {
        const row = ws.addRow(r);
        row.eachCell((cell, col) => {
            cell.font = { color: { argb: COL.navy.argb }, size: 10, name: "Calibri" };
            if (i % 2 === 1) {
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: COL.zebra.argb } };
            }
            cell.alignment = { vertical: "top", wrapText: !!columns[col - 1]?.wrap };
            cell.border = { bottom: { style: "hair", color: { argb: COL.line.argb } } };
        });
        if (statusKey && r[statusKey]) {
            const s = String(r[statusKey]).toLowerCase();
            const cell = row.getCell(statusKey);
            if (s === "closed")    cell.font = { bold: true, color: { argb: "FF15803D" }, size: 10 };
            else if (s === "cancelled") cell.font = { bold: true, color: { argb: "FFB91C1C" }, size: 10 };
            else if (s === "open")  cell.font = { bold: true, color: { argb: "FF1D4ED8" }, size: 10 };
            else                     cell.font = { bold: true, color: { argb: COL.orange.argb }, size: 10 };
        }
    });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ── PDF ──────────────────────────────────────────────────────────────
export function exportTicketsPdf({
    moduleName,
    sheetTitle,
    columns,
    rows,
    filterLabel,
    statusKey,        // optional: colour-code this column's body cells
    filename,
}) {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Navy header band
    doc.setFillColor(...COL.navy.rgb);
    doc.rect(0, 0, pageWidth, 60, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text("Dolluz Corp", 28, 32);
    const wDol = doc.getTextWidth("Dolluz Corp");
    doc.setTextColor(...COL.orange.rgb);
    doc.text(".", 28 + wDol, 32);
    const wDot = doc.getTextWidth(".");
    doc.text(`    ${moduleName}`, 28 + wDol + wDot, 32);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COL.sub.rgb);
    doc.text(sheetTitle, 28, 50);

    // Meta lines
    let metaY = 86;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    if (filterLabel) {
        doc.setTextColor(...COL.slate.rgb);
        doc.text("Filter:", 28, metaY);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COL.navy.rgb);
        doc.text(filterLabel, 70, metaY);
        doc.setFont("helvetica", "normal");
        metaY += 14;
    }
    doc.setTextColor(...COL.slate.rgb);
    doc.text("Generated:", 28, metaY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COL.navy.rgb);
    doc.text(fmtTs(new Date().toISOString()) + " IST", 90, metaY);
    metaY += 14;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COL.slate.rgb);
    doc.text("Rows:", 28, metaY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COL.navy.rgb);
    doc.text(String(rows.length), 70, metaY);

    // Table. Fill the full page width and let columns auto-size to at least
    // their header/content width, so single-word headers ("Downloaded",
    // "Deductions") never wrap/cut in half. pdfWidth is used only as a minimum
    // hint - columns grow beyond it to fill the page.
    const tableWidth = pageWidth - 56;   // 28pt margin each side
    autoTable(doc, {
        startY: metaY + 12,
        margin: { left: 28, right: 28 },
        tableWidth,
        head: [columns.map(c => c.label)],
        body: rows.map(r => columns.map(c => (r[c.key] == null ? "" : String(r[c.key])))),
        theme: "plain",
        styles: {
            font: "helvetica",
            fontSize: 9.5,
            cellPadding: { top: 7, right: 9, bottom: 7, left: 9 },
            textColor: COL.navy.rgb,
            lineColor: COL.line.rgb,
            lineWidth: 0.4,
            valign: "middle",
            overflow: "linebreak",
        },
        headStyles: {
            fillColor: COL.navy.rgb,
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 10.5,
            halign: "left",
            valign: "middle",
            cellPadding: { top: 9, right: 9, bottom: 9, left: 9 },
            lineWidth: 0,
        },
        bodyStyles: { minCellHeight: 20 },
        alternateRowStyles: { fillColor: COL.zebra.rgb },
        columnStyles: columns.reduce((acc, c, i) => {
            const st = {};
            if (c.pdfWidth) st.minCellWidth = c.pdfWidth;   // minimum, not a hard cap
            if (c.align) st.halign = c.align;
            if (Object.keys(st).length) acc[i] = st;
            return acc;
        }, {}),
        // Colour-code the status column body cells (matches the Excel look).
        didParseCell(data) {
            if (!statusKey || data.section !== "body") return;
            if (columns[data.column.index]?.key !== statusKey) return;
            const s = String(data.cell.raw || "").toLowerCase();
            data.cell.styles.fontStyle = "bold";
            if (s === "published" || s === "closed" || s === "approved" || s === "paid") {
                data.cell.styles.textColor = [21, 128, 61];      // green
            } else if (s === "cancelled" || s === "rejected") {
                data.cell.styles.textColor = [185, 28, 28];      // red
            } else if (s === "open") {
                data.cell.styles.textColor = [29, 78, 216];      // blue
            } else {
                data.cell.styles.textColor = COL.orange.rgb;     // Draft / Pending / etc.
            }
        },
    });

    addEagleWatermark(doc);
    doc.save(filename);
}
